import { FC, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
  Switch,
} from "@capacit-com/lara-law-shared-ui";
import { ChevronLeft, ChevronRight, Megaphone, Wrench } from "lucide-react";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useReleaseNotes } from "@/hooks/useReleaseNotes";
import useReleaseNotesStore from "@/stores/release-notes.store";
import { ReleaseNote } from "@/models/release-notes/ReleaseNote";
import {
  groupReleaseNotesByMonth,
  visibleReleaseNotes,
} from "@/lib/releaseNotesVisibility";
import ReleaseNoteMarkdown from "./ReleaseNoteMarkdown";
import { formatReleaseDate } from "./releaseNoteDate";

/**
 * The product changelog ("Ændringslog"), opened from the profile menu like the
 * notification settings dialog. Reads are open to every user; authoring happens
 * through the release-notes publisher, so this is view-only.
 *
 * Two panes: the month-grouped version list on the left and the selected
 * release's article on the right, so a reader moves between releases without
 * leaving the article. Below the `md` breakpoint the panes take turns: the list
 * first, the article after a tap, "Alle ændringer" back again.
 *
 * Technical releases (nothing a user can see) are hidden until the user flips
 * "Vis tekniske opdateringer"; the choice is a saved preference.
 */

const TEXT = {
  title: "Ændringslog",
  description: "Nyheder og forbedringer i LODA.",
  back: "Alle ændringer",
  empty: "Der er endnu ingen ændringer at vise.",
  allTechnicalHidden:
    "Alle ændringer er tekniske. Slå tekniske opdateringer til for at se dem.",
  loadError: "Ændringsloggen kunne ikke indlæses.",
  retry: "Prøv igen",
  newer: "Nyere",
  older: "Ældre",
  showTechnical: "Vis tekniske opdateringer",
  technicalHint: "Tekniske opdateringer ændrer ikke noget, du kan se i LODA",
  hiddenCount: (count: number) =>
    count === 1
      ? "1 teknisk opdatering er skjult"
      : `${count} tekniske opdateringer er skjult`,
  technicalBadge: "Teknisk",
  technicalBannerTitle: "Teknisk opdatering",
  technicalBannerBody:
    "Denne version ændrer ikke noget, du kan se i LODA. Du kan skjule tekniske opdateringer i listen.",
  newSinceLastVisit: "Ny siden sidst",
  position: (index: number, total: number) => `${index} af ${total}`,
} as const;

const SHOW_TECHNICAL_SWITCH_ID = "release-notes-show-technical";

/**
 * Mounted once in the router, beside the sidebar; the profile menu opens it
 * through the store. Mounting it inside the menu lost it on a resize across
 * the `md` breakpoint, where the sidebar swaps to a sheet.
 */
const ReleaseNotesDialog: FC = () => {
  const open = useReleaseNotesStore((state) => state.isDialogOpen);
  const onOpenChange = useReleaseNotesStore((state) => state.setDialogOpen);
  const { data: notes, isPending, isError, error, refetch } = useReleaseNotes();
  const isMobile = useIsMobile();
  const markReleaseNotesSeen = useReleaseNotesStore(
    (state) => state.markReleaseNotesSeen
  );
  const showTechnical = useReleaseNotesStore(
    (state) => state.showTechnicalReleaseNotes
  );
  const setShowTechnical = useReleaseNotesStore(
    (state) => state.setShowTechnicalReleaseNotes
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * The last-seen id as it was when the dialog opened. The "new since last
   * visit" dots are drawn against this snapshot, because opening the dialog
   * marks everything seen straight away (see below) and the dots would vanish
   * before the user had read the list.
   */
  const [seenAtOpen, setSeenAtOpen] = useState<string | null>(null);

  // Always land on the list (mobile) / the newest release (desktop) when reopened.
  useEffect(() => {
    if (open) {
      setSeenAtOpen(useReleaseNotesStore.getState().lastSeenReleaseNoteId);
    } else {
      setSelectedId(null);
    }
  }, [open]);

  // Opening the changelog marks the newest release as seen, clearing the
  // unread dot on the profile menu. Runs again if notes arrive after opening.
  useEffect(() => {
    if (!open) return;
    const newest = notes?.[0];
    if (newest) markReleaseNotesSeen(newest.id);
  }, [open, notes, markReleaseNotesSeen]);

  useEffect(() => {
    if (!isError) return;
    logger.error("Failed to load release notes", {
      operation: "getReleaseNotes",
      error,
    });
  }, [isError, error]);

  const visible = useMemo(
    () => visibleReleaseNotes(notes ?? [], showTechnical),
    [notes, showTechnical]
  );
  const groups = useMemo(() => groupReleaseNotesByMonth(visible), [visible]);
  const hiddenCount = (notes?.length ?? 0) - visible.length;

  // Ids newer than the release the user had seen before this visit.
  const newIds = useMemo(() => {
    if (!notes || !seenAtOpen) return new Set<string>();
    const seenIndex = notes.findIndex((note) => note.id === seenAtOpen);
    if (seenIndex < 0) return new Set<string>();
    return new Set(notes.slice(0, seenIndex).map((note) => note.id));
  }, [notes, seenAtOpen]);

  // A selection the filter just hid falls back to the newest visible release;
  // on a phone nothing is selected until the user taps a release.
  const selected: ReleaseNote | null = useMemo(() => {
    const chosen = selectedId
      ? visible.find((note) => note.id === selectedId)
      : undefined;
    if (chosen) return chosen;
    return isMobile ? null : (visible[0] ?? null);
  }, [visible, selectedId, isMobile]);

  const selectedIndex = selected ? visible.indexOf(selected) : -1;
  const newer = selectedIndex > 0 ? visible[selectedIndex - 1] : undefined;
  const older =
    selectedIndex >= 0 && selectedIndex < visible.length - 1
      ? visible[selectedIndex + 1]
      : undefined;

  const showList = !isMobile || selected === null;
  const showArticle = !isMobile || selected !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="full"
        className="flex h-[min(720px,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="shrink-0 border-b border-border px-6 py-5 text-left">
          <div className="flex items-center gap-3.5">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary">
              <Megaphone className="size-5" />
            </span>
            <div className="flex flex-col gap-0.5">
              <DialogTitle className="text-xl font-semibold">
                {TEXT.title}
              </DialogTitle>
              <DialogDescription>{TEXT.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {isPending ? (
          <div className="flex flex-1 items-center justify-center py-8">
            <Spinner />
          </div>
        ) : isError && !notes ? (
          <div className="flex flex-col items-start gap-3 px-6 py-6">
            <p className="text-sm text-muted-foreground">{TEXT.loadError}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {TEXT.retry}
            </Button>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            <aside
              className={cn(
                "flex w-full shrink-0 flex-col border-border bg-muted/30 md:w-[328px] md:border-r",
                !showList && "hidden"
              )}
            >
              <div className="flex flex-col gap-1 border-b border-border px-4 pt-3.5 pb-3">
                <div className="flex items-center justify-between gap-3">
                  <label
                    htmlFor={SHOW_TECHNICAL_SWITCH_ID}
                    className="cursor-pointer text-sm font-medium text-foreground"
                  >
                    {TEXT.showTechnical}
                  </label>
                  <Switch
                    id={SHOW_TECHNICAL_SWITCH_ID}
                    checked={showTechnical}
                    onCheckedChange={setShowTechnical}
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  {hiddenCount > 0
                    ? TEXT.hiddenCount(hiddenCount)
                    : TEXT.technicalHint}
                </span>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pt-2 pb-4">
                {groups.length === 0 ? (
                  <p className="px-2 py-6 text-sm text-muted-foreground">
                    {notes && notes.length > 0
                      ? TEXT.allTechnicalHidden
                      : TEXT.empty}
                  </p>
                ) : (
                  groups.map((group) => (
                    <section key={group.key}>
                      <div className="flex items-center gap-2.5 px-2 pt-3.5 pb-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {group.label}
                        </span>
                        <span className="h-px flex-1 bg-border" />
                      </div>
                      <ul className="flex flex-col">
                        {group.notes.map((note) => {
                          const isSelected = note.id === selected?.id;
                          return (
                            <li key={note.id}>
                              <button
                                type="button"
                                aria-current={isSelected ? "true" : undefined}
                                onClick={() => setSelectedId(note.id)}
                                className={cn(
                                  "flex w-full items-center justify-between gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors",
                                  isSelected
                                    ? "border-primary/30 bg-primary/10"
                                    : "border-transparent hover:bg-muted"
                                )}
                              >
                                <span className="flex min-w-0 flex-col gap-px">
                                  <span className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-foreground">
                                      {note.title}
                                    </span>
                                    {newIds.has(note.id) && (
                                      <span
                                        className="size-[7px] rounded-full bg-primary"
                                        role="img"
                                        aria-label={TEXT.newSinceLastVisit}
                                      />
                                    )}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatReleaseDate(note.releaseDate)}
                                  </span>
                                </span>
                                <span className="flex shrink-0 items-center gap-2">
                                  {note.isTechnical && (
                                    <Badge
                                      variant="gray"
                                      className="gap-1 text-[11px] font-semibold"
                                    >
                                      <Wrench className="size-3" />
                                      {TEXT.technicalBadge}
                                    </Badge>
                                  )}
                                  {isMobile && (
                                    <ChevronRight className="size-4 text-muted-foreground" />
                                  )}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))
                )}
              </div>
            </aside>

            <section
              className={cn(
                "flex min-w-0 flex-1 flex-col",
                !showArticle && "hidden"
              )}
            >
              {selected && (
                <>
                  <article className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 pb-6 md:px-10 md:pt-7">
                    <div className="flex max-w-[640px] flex-col">
                      {isMobile && (
                        <button
                          type="button"
                          onClick={() => setSelectedId(null)}
                          className="mb-3 inline-flex min-h-11 items-center gap-1 self-start text-sm font-medium text-primary hover:opacity-80"
                        >
                          <ChevronLeft className="size-4" />
                          {TEXT.back}
                        </button>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatReleaseDate(selected.releaseDate)}
                      </p>
                      <h2 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                        {selected.title}
                      </h2>

                      {selected.isTechnical && (
                        <div className="mt-4 flex items-start gap-3 rounded-[10px] border border-border bg-muted px-3.5 py-3 text-foreground">
                          <Wrench className="mt-0.5 size-4 shrink-0" />
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-semibold">
                              {TEXT.technicalBannerTitle}
                            </span>
                            <span className="text-xs">
                              {TEXT.technicalBannerBody}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="mt-2">
                        <ReleaseNoteMarkdown body={selected.body} />
                      </div>
                    </div>
                  </article>

                  <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-4 py-3 md:px-6">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!newer}
                      onClick={() => newer && setSelectedId(newer.id)}
                    >
                      <ChevronLeft className="size-4" />
                      {TEXT.newer}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {TEXT.position(selectedIndex + 1, visible.length)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!older}
                      onClick={() => older && setSelectedId(older.id)}
                    >
                      {TEXT.older}
                      <ChevronRight className="size-4" />
                    </Button>
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseNotesDialog;
